/**
 * Unified Scan Engine Hook
 *
 * Core logic for the unified scanning system. Discovers [data-scan-item]
 * elements via MutationObserver, handles hierarchical / spatial navigation
 * modes, renders focus via a React overlay (no DOM classList), and manages
 * auto-timer for automatic scanning.
 *
 * Hierarchical scanning uses a 3-level model:
 *   Level 1 — zone:  main content vs sidebar
 *   Level 2 — row:   rows within the selected zone (sorted top-to-bottom)
 *   Level 3 — item:  individual items within the selected row
 *
 * When only one zone has scan items, the zone level is auto-skipped.
 * After any item click, the scanner resets to zone level so the user
 * starts fresh (standard AAC row/column scanning behavior).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  discoverScanItems,
  flattenItems,
  DiscoveredItems,
  ScanItem,
} from '@/lib/scanItemDiscovery';
import { getUserSettings } from '@/lib/userSettings';
import { isCaregiverRoute } from '@/lib/caregiverRoutes';
import { checkIsScanningMode } from '@/lib/scanningHelpers';
import { getExternalSwitchAction } from './useExternalSwitchInput';
import type { ScanningAction } from '@/lib/scanningSystem';

// ── Types ────────────────────────────────────────────────────────────────────

type NavigationMode = 'hierarchical' | 'spatial';
type HierarchicalLevel = 'zone' | 'row' | 'item';

interface ScanCursor {
  zone: 'main' | 'sidebar';
  rowIndex: number;
  itemIndex: number;
  level: HierarchicalLevel;
}

export interface FocusOverlay {
  elements: HTMLElement[];
  type: 'item' | 'row' | 'zone';
}

export interface ScanEngineState {
  isActive: boolean;
  refreshItems: () => void;
  focusOverlay: FocusOverlay | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function getInputCount(): number {
  const s = getUserSettings();
  if (s.controlMethod === 'external_switch') return s.externalSwitchCount || 1;
  if (s.controlMethod === 'mouth_gestures') return s.mouthGestures?.length || 1;
  if (s.controlMethod === 'eye_control' || s.controlMethod === 'eyecontrol')
    return s.eyeControlGestures?.length || 1;
  return 1;
}

function getNavigationMode(inputCount: number): NavigationMode {
  if (inputCount >= 5) return 'spatial';
  return 'hierarchical';
}

function shouldAutoScan(): boolean {
  const s = getUserSettings();
  const count = getInputCount();
  return count <= 2 || s.scanControlMode === 'automatic';
}

function getZoneRows(items: DiscoveredItems, zone: 'main' | 'sidebar'): ScanItem[][] {
  return zone === 'main' ? items.main : items.sidebar;
}

function makeCursor(): ScanCursor {
  return { zone: 'main', rowIndex: 0, itemIndex: 0, level: 'zone' };
}

/** Check if both zones have scannable items */
function hasBothZones(items: DiscoveredItems): boolean {
  const mainCount = items.main.reduce((s, r) => s + r.length, 0);
  const sidebarCount = items.sidebar.reduce((s, r) => s + r.length, 0);
  return mainCount > 0 && sidebarCount > 0;
}

/**
 * Click a scan item. If the element itself isn't a button/link,
 * find the first clickable descendant (the actual ActionCard, etc.)
 * and click that instead — .click() on a wrapper div won't propagate
 * down to child onClick handlers.
 */
function clickScanItem(el: HTMLElement): void {
  if (!el.isConnected) return;
  const tag = el.tagName;
  if (tag === 'BUTTON' || tag === 'A' || el.getAttribute('role') === 'button') {
    el.click();
    return;
  }
  const clickable = el.querySelector<HTMLElement>('button, a, [role="button"]');
  if (clickable) {
    clickable.click();
  } else {
    el.click();
  }
}

/**
 * Find (zone, rowIndex, itemIndex) of an element across both zones.
 */
function findItemPosition(
  items: DiscoveredItems,
  el: HTMLElement
): { zone: 'main' | 'sidebar'; rowIndex: number; itemIndex: number } | null {
  for (const zone of ['main', 'sidebar'] as const) {
    const rows = zone === 'main' ? items.main : items.sidebar;
    for (let r = 0; r < rows.length; r++) {
      for (let i = 0; i < rows[r].length; i++) {
        if (rows[r][i].element === el) return { zone, rowIndex: r, itemIndex: i };
      }
    }
  }
  return null;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useScanEngine(isRTL: boolean): ScanEngineState {
  const location = useLocation();
  const [isActive, setIsActive] = useState(false);

  // React state for focus overlay — drives the overlay component in context
  const [focusOverlay, setFocusOverlay] = useState<FocusOverlay | null>(null);

  // All mutable engine state lives in refs (no re-renders on cursor move)
  const itemsRef = useRef<DiscoveredItems>({ sidebar: [], main: [] });
  const cursorRef = useRef<ScanCursor>(makeCursor());
  const isActiveRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const discoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const itemCycleCountRef = useRef(0);

  // Ref for setFocusOverlay so timer closures always call the latest version
  const setFocusOverlayRef = useRef(setFocusOverlay);
  setFocusOverlayRef.current = setFocusOverlay;

  // ── Focus rendering (React state, no DOM classList) ──────────────────────

  const renderFocus = useCallback(() => {
    if (!isActiveRef.current) {
      setFocusOverlayRef.current(null);
      return;
    }

    const items = itemsRef.current;
    const cursor = cursorRef.current;
    const mode = getNavigationMode(getInputCount());

    if (mode === 'hierarchical') {
      if (cursor.level === 'zone') {
        // Highlight the entire zone
        const zoneEl = document.querySelector<HTMLElement>(`[data-scan-zone="${cursor.zone}"]`);
        if (zoneEl) {
          setFocusOverlayRef.current({ elements: [zoneEl], type: 'zone' });
        } else {
          setFocusOverlayRef.current(null);
        }
      } else if (cursor.level === 'row') {
        const row = getZoneRows(items, cursor.zone)[cursor.rowIndex];
        if (row && row.length > 0) {
          setFocusOverlayRef.current({ elements: row.map(i => i.element), type: 'row' });
          row[0].element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } else {
          setFocusOverlayRef.current(null);
        }
      } else {
        // item level
        const item = getZoneRows(items, cursor.zone)[cursor.rowIndex]?.[cursor.itemIndex];
        if (item) {
          setFocusOverlayRef.current({ elements: [item.element], type: 'item' });
          item.element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } else {
          setFocusOverlayRef.current(null);
        }
      }
    } else {
      // spatial — always show single item
      const item = getZoneRows(items, cursor.zone)[cursor.rowIndex]?.[cursor.itemIndex];
      if (item) {
        setFocusOverlayRef.current({ elements: [item.element], type: 'item' });
        item.element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } else {
        setFocusOverlayRef.current(null);
      }
    }
  }, []);

  // ── Post-click reset ────────────────────────────────────────────────────
  // After clicking a scan item, reset to zone level so user starts fresh.
  // This is standard AAC behavior — the page content may change after a click.

  const resetAfterClickRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetToZone = useCallback(() => {
    const cursor = cursorRef.current;
    cursor.level = 'zone';
    cursor.zone = 'main';
    cursor.rowIndex = 0;
    cursor.itemIndex = 0;
    itemCycleCountRef.current = 0;
  }, []);

  const scheduleResetAfterClick = useCallback(() => {
    if (resetAfterClickRef.current) clearTimeout(resetAfterClickRef.current);
    resetAfterClickRef.current = setTimeout(() => {
      resetToZone();
      renderFocus();
      // Restart timer from zone level
      if (isActiveRef.current && shouldAutoScan()) {
        startTimerRef.current();
      }
    }, 300);
  }, [resetToZone, renderFocus]);

  // ── Navigation handlers ────────────────────────────────────────────────

  const handleHierarchicalAction = useCallback((action: ScanningAction) => {
    const items = itemsRef.current;
    const cursor = cursorRef.current;
    const maxCycles = getUserSettings().cellLevelMaxCycles || 2;
    const bothZones = hasBothZones(items);

    switch (cursor.level) {
      case 'zone': {
        if (action === 'next' || action === 'prev') {
          cursor.zone = cursor.zone === 'main' ? 'sidebar' : 'main';
          // Skip empty zones
          const rows = getZoneRows(items, cursor.zone);
          if (rows.length === 0) {
            cursor.zone = cursor.zone === 'main' ? 'sidebar' : 'main';
          }
        } else if (action === 'select') {
          const rows = getZoneRows(items, cursor.zone);
          if (rows.length > 0) {
            cursor.rowIndex = 0;
            if (rows.length === 1) {
              const row = rows[0];
              if (row.length === 1) {
                // Single item in single row — click directly
                clickScanItem(row[0].element);
                scheduleResetAfterClick();
              } else {
                // Skip row level, go to items
                cursor.level = 'item';
                cursor.itemIndex = 0;
                itemCycleCountRef.current = 0;
              }
            } else {
              cursor.level = 'row';
            }
          }
        } else if (action === 'cancel') {
          cursor.zone = 'main';
        }
        break;
      }

      case 'row': {
        const rows = getZoneRows(items, cursor.zone);
        if (rows.length === 0) { cursor.level = 'zone'; break; }

        if (action === 'next') {
          cursor.rowIndex = (cursor.rowIndex + 1) % rows.length;
        } else if (action === 'prev') {
          cursor.rowIndex = (cursor.rowIndex - 1 + rows.length) % rows.length;
        } else if (action === 'select') {
          const row = rows[cursor.rowIndex];
          if (row && row.length > 0) {
            if (row.length === 1) {
              // Single item — click directly
              clickScanItem(row[0].element);
              scheduleResetAfterClick();
            } else {
              cursor.level = 'item';
              cursor.itemIndex = 0;
              itemCycleCountRef.current = 0;
            }
          }
        } else if (action === 'back' || action === 'cancel') {
          if (bothZones) {
            cursor.level = 'zone';
          } else {
            // Only one zone — stay at row, reset to first
            cursor.rowIndex = 0;
          }
        }
        break;
      }

      case 'item': {
        const rows = getZoneRows(items, cursor.zone);
        const row = rows[cursor.rowIndex];
        if (!row || row.length === 0) { cursor.level = 'row'; break; }

        if (action === 'next') {
          cursor.itemIndex = (cursor.itemIndex + 1) % row.length;
          // Track wrap-around for auto-return after max cycles
          if (cursor.itemIndex === 0) {
            itemCycleCountRef.current++;
            if (itemCycleCountRef.current >= maxCycles) {
              cursor.level = 'row';
              itemCycleCountRef.current = 0;
            }
          }
        } else if (action === 'prev') {
          cursor.itemIndex = (cursor.itemIndex - 1 + row.length) % row.length;
        } else if (action === 'select') {
          const el = row[cursor.itemIndex]?.element;
          if (el) {
            clickScanItem(el);
            scheduleResetAfterClick();
          }
        } else if (action === 'back' || action === 'cancel') {
          cursor.level = 'row';
          itemCycleCountRef.current = 0;
        }
        break;
      }
    }
  }, [scheduleResetAfterClick]);

  const handleSpatialAction = useCallback((action: ScanningAction) => {
    const items = itemsRef.current;
    const cursor = cursorRef.current;

    if (action === 'select') {
      const el = getZoneRows(items, cursor.zone)[cursor.rowIndex]?.[cursor.itemIndex]?.element;
      if (el) {
        clickScanItem(el);
        scheduleResetAfterClick();
      }
      return;
    }

    // Map action to physical screen direction
    let direction: 'up' | 'down' | 'left' | 'right' | null = null;
    if (action === 'up') direction = 'up';
    else if (action === 'down') direction = 'down';
    else if (action === 'left') direction = 'left';
    else if (action === 'right') direction = 'right';
    else if (action === 'next') direction = isRTL ? 'left' : 'right';
    else if (action === 'prev') direction = isRTL ? 'right' : 'left';

    if (!direction) return;

    const currentItem = getZoneRows(items, cursor.zone)[cursor.rowIndex]?.[cursor.itemIndex];
    if (!currentItem) return;

    const cx = currentItem.rect.left + currentItem.rect.width / 2;
    const cy = currentItem.rect.top + currentItem.rect.height / 2;

    let bestItem: ScanItem | null = null;
    let bestDist = Infinity;

    for (const item of flattenItems(items)) {
      if (item.element === currentItem.element) continue;

      const ix = item.rect.left + item.rect.width / 2;
      const iy = item.rect.top + item.rect.height / 2;

      let inDirection = false;
      const tolerance = 5;
      switch (direction) {
        case 'up':    inDirection = iy < cy - tolerance; break;
        case 'down':  inDirection = iy > cy + tolerance; break;
        case 'left':  inDirection = ix < cx - tolerance; break;
        case 'right': inDirection = ix > cx + tolerance; break;
      }
      if (!inDirection) continue;

      const dist = Math.hypot(ix - cx, iy - cy);
      if (dist < bestDist) {
        bestDist = dist;
        bestItem = item;
      }
    }

    if (bestItem) {
      const pos = findItemPosition(items, bestItem.element);
      if (pos) {
        cursor.zone = pos.zone;
        cursor.rowIndex = pos.rowIndex;
        cursor.itemIndex = pos.itemIndex;
      }
    }
  }, [isRTL, scheduleResetAfterClick]);

  // ── Main action dispatcher ─────────────────────────────────────────────

  const handleAction = useCallback((action: ScanningAction) => {
    if (!isActiveRef.current) return;

    const mode = getNavigationMode(getInputCount());

    if (mode === 'hierarchical') {
      handleHierarchicalAction(action);
    } else {
      handleSpatialAction(action);
    }

    renderFocus();
  }, [handleHierarchicalAction, handleSpatialAction, renderFocus]);

  // ── Auto-timer ─────────────────────────────────────────────────────────

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    if (!isActiveRef.current) return;

    const mode = getNavigationMode(getInputCount());
    if (mode === 'spatial') return; // no auto-scan for spatial
    if (!shouldAutoScan()) return;

    const speed = (getUserSettings().scanSpeed || 3) * 1000;

    timerRef.current = setInterval(() => {
      if (!isActiveRef.current) { stopTimer(); return; }

      const items = itemsRef.current;
      const cursor = cursorRef.current;

      if (cursor.level === 'zone') {
        if (!hasBothZones(items)) {
          // Only one zone — auto-select into it
          handleHierarchicalAction('select');
        } else {
          handleHierarchicalAction('next');
        }
      } else if (cursor.level === 'row') {
        const rows = getZoneRows(items, cursor.zone);
        if (rows.length === 1) {
          // Only 1 row — auto-select into items
          handleHierarchicalAction('select');
        } else {
          handleHierarchicalAction('next');
        }
      } else {
        // item level — always advance
        handleHierarchicalAction('next');
      }

      renderFocus();
    }, speed);
  }, [stopTimer, renderFocus, handleHierarchicalAction]);

  // ── Discovery ──────────────────────────────────────────────────────────

  const discover = useCallback(() => {
    const pathname = window.location.pathname;
    const currentSettings = getUserSettings();
    const isScanningEnabled =
      !isCaregiverRoute(pathname) &&
      checkIsScanningMode(null, currentSettings.controlMethod);

    const items = discoverScanItems(document.documentElement, isRTL);
    itemsRef.current = items;

    const mainItemCount = items.main.reduce((sum, row) => sum + row.length, 0);
    const sidebarItemCount = items.sidebar.reduce((sum, row) => sum + row.length, 0);
    const hasMainItems = mainItemCount > 0;
    const nowActive = hasMainItems && isScanningEnabled;
    const wasActive = isActiveRef.current;
    isActiveRef.current = nowActive;

    if (nowActive !== wasActive) {
      setIsActive(nowActive);
    }

    if (nowActive) {
      // Clamp cursor to valid bounds
      const cursor = cursorRef.current;
      const rows = getZoneRows(items, cursor.zone);

      if (cursor.level !== 'zone') {
        if (rows.length === 0) {
          cursor.level = 'zone';
        } else {
          if (cursor.rowIndex >= rows.length) cursor.rowIndex = 0;
          const row = rows[cursor.rowIndex];
          if (row && cursor.itemIndex >= row.length) cursor.itemIndex = 0;
        }
      }

      renderFocus();
      if (!timerRef.current) {
        startTimer();
      }
    } else {
      setFocusOverlayRef.current(null);
      stopTimer();
    }
  }, [isRTL, renderFocus, startTimer, stopTimer]);

  // Second-pass timeout ref — ensures all items are found after animations complete
  const secondPassRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedDiscover = useCallback(() => {
    if (discoverTimeoutRef.current) clearTimeout(discoverTimeoutRef.current);
    if (secondPassRef.current) clearTimeout(secondPassRef.current);

    discoverTimeoutRef.current = setTimeout(() => {
      discover();
      // Schedule a second discovery pass after Framer Motion staggered
      // animations complete. Items with staggered delays (index * 30ms)
      // may still be at opacity: 0 during the first pass and get filtered
      // out by isVisible, causing incomplete row detection.
      secondPassRef.current = setTimeout(discover, 600);
    }, 250);
  }, [discover]);

  // ── Event listeners ────────────────────────────────────────────────────

  const handleActionRef = useRef(handleAction);
  handleActionRef.current = handleAction;
  const startTimerRef = useRef(startTimer);
  startTimerRef.current = startTimer;

  // Listen to scanningAction events (dispatched by useEyeControlInput)
  useEffect(() => {
    const handler = (e: Event) => {
      if (!isActiveRef.current) return;
      const action = (e as CustomEvent<{ action: ScanningAction }>).detail.action;

      if (shouldAutoScan()) {
        startTimerRef.current();
      }

      handleActionRef.current(action);
    };

    window.addEventListener('scanningAction', handler);
    return () => window.removeEventListener('scanningAction', handler);
  }, []);

  // Listen to keyboard events for external switch input
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!isActiveRef.current) return;

      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;

      const settings = getUserSettings();
      if (settings.controlMethod !== 'external_switch') return;

      const rawAction = getExternalSwitchAction(e);
      if (!rawAction) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const action: ScanningAction = rawAction === 'previous' ? 'prev' : rawAction as ScanningAction;

      if (shouldAutoScan()) {
        startTimerRef.current();
      }

      handleActionRef.current(action);
    };

    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, []);

  // ── MutationObserver ───────────────────────────────────────────────────

  useEffect(() => {
    debouncedDiscover();

    const mutationObserver = new MutationObserver(debouncedDiscover);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-scan-item', 'hidden', 'disabled', 'aria-disabled'],
    });

    const resizeObserver = new ResizeObserver(debouncedDiscover);
    resizeObserver.observe(document.body);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      if (discoverTimeoutRef.current) clearTimeout(discoverTimeoutRef.current);
      if (secondPassRef.current) clearTimeout(secondPassRef.current);
    };
  }, [debouncedDiscover]);

  // ── Route change reset ─────────────────────────────────────────────────

  useEffect(() => {
    cursorRef.current = makeCursor();
    itemCycleCountRef.current = 0;
    if (resetAfterClickRef.current) clearTimeout(resetAfterClickRef.current);
    if (secondPassRef.current) clearTimeout(secondPassRef.current);
    setFocusOverlay(null);
    stopTimer();

    const timer = setTimeout(discover, 200);
    return () => clearTimeout(timer);
  }, [location.pathname, stopTimer, discover]);

  // ── Settings change listener ───────────────────────────────────────────

  useEffect(() => {
    const handler = () => {
      stopTimer();
      debouncedDiscover();
    };
    window.addEventListener('userSettingsChanged', handler);
    window.addEventListener('controlMethodChanged', handler);
    return () => {
      window.removeEventListener('userSettingsChanged', handler);
      window.removeEventListener('controlMethodChanged', handler);
    };
  }, [debouncedDiscover, stopTimer]);

  // ── Cleanup on unmount ─────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      stopTimer();
      if (resetAfterClickRef.current) clearTimeout(resetAfterClickRef.current);
      if (secondPassRef.current) clearTimeout(secondPassRef.current);
    };
  }, [stopTimer]);

  return { isActive, refreshItems: discover, focusOverlay };
}
